import React, { useState } from 'react';
import './Partner.css';

function Partner() {
  const [activeTab, setActiveTab] = useState('healthcare');
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    partnerType: '',
    location: '',
    message: '',
    heardAbout: ''
  });

  const partnerTypes = [
    {
      id: 'healthcare',
      title: 'Healthcare Providers',
      icon: '🏥',
      description: 'Hospitals, clinics, therapy centers, aged care facilities',
      benefits: [
        'Special pricing for healthcare institutions',
        'Bulk ordering discounts',
        'Staff training and product education',
        'Custom adaptive solutions',
        'NDIS/HCP integration support'
      ],
      idealFor: [
        'Occupational therapists',
        'Physiotherapy clinics',
        'Rehabilitation centers',
        'Aged care facilities',
        'Disability service providers'
      ]
    },
    {
      id: 'retail',
      title: 'Retail Partners',
      icon: '🛍️',
      description: 'Boutiques, department stores, specialty retailers',
      benefits: [
        'Competitive wholesale margins',
        'Marketing and promotional support',
        'Visual merchandising materials',
        'Consignment options available',
        'Seasonal product rotations'
      ],
      idealFor: [
        'Adaptive fashion boutiques',
        'Inclusive clothing stores',
        'Department stores',
        'Specialty retailers',
        'Online marketplaces'
      ]
    },
    {
      id: 'corporate',
      title: 'Corporate & Institutions',
      icon: '🏢',
      description: 'Schools, universities, government, corporate wellness',
      benefits: [
        'Volume discounts for bulk orders',
        'Custom branding options',
        'Employee wellness programs',
        'Educational resources',
        'Bespoke uniform solutions'
      ],
      idealFor: [
        'Educational institutions',
        'Government departments',
        'Corporate wellness programs',
        'Community organizations',
        'Non-profit organizations'
      ]
    },
    {
      id: 'distributor',
      title: 'Distributors',
      icon: '🌍',
      description: 'Regional and international distribution partners',
      benefits: [
        'Exclusive territory rights',
        'Marketing development funds',
        'Stock rotation support',
        'Sales training programs',
        'Priority product allocation'
      ],
      idealFor: [
        'International distributors',
        'Regional wholesalers',
        'Import/export companies',
        'Healthcare distributors',
        'Disability product suppliers'
      ]
    }
  ];

  const testimonials = [
    {
      quote: "Partnering with The Shapes United has allowed us to offer our patients high-quality adaptive clothing that truly makes a difference in their recovery and daily comfort.",
      author: "Dr. Sarah Chen",
      role: "Director, Melbourne Rehabilitation Centre",
      type: "Healthcare Provider"
    },
    {
      quote: "The wholesale program is well-structured and the support from their team has been exceptional. Our customers love the quality and innovative designs.",
      author: "Michael Rodriguez",
      role: "Owner, Inclusive Fashion Boutique",
      type: "Retail Partner"
    },
    {
      quote: "As a school for children with special needs, the adaptive clothing has been transformative for our students' independence and confidence.",
      author: "Jessica Williams",
      role: "Principal, Sunshine Special School",
      type: "Educational Institution"
    }
  ];

  const partnershipProcess = [
    {
      step: 1,
      title: "Initial Inquiry",
      description: "Complete our partnership application or contact our wholesale team",
      duration: "1-2 business days"
    },
    {
      step: 2,
      title: "Consultation",
      description: "Personalized meeting to discuss your needs and our partnership options",
      duration: "30-60 minutes"
    },
    {
      step: 3,
      title: "Proposal & Agreement",
      description: "Receive customized partnership proposal and terms",
      duration: "3-5 business days"
    },
    {
      step: 4,
      title: "Onboarding & Training",
      description: "Complete setup, receive training, and place first order",
      duration: "1-2 weeks"
    },
    {
      step: 5,
      title: "Ongoing Support",
      description: "Regular check-ins, marketing support, and partnership growth",
      duration: "Ongoing"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, you would submit this data to your backend
    alert('Thank you for your partnership interest! Our team will contact you within 48 hours.');
    setFormData({
      name: '',
      organization: '',
      email: '',
      phone: '',
      partnerType: '',
      location: '',
      message: '',
      heardAbout: ''
    });
  };

  return (
    <div className="partner-page">
      {/* Hero Image Section - 70% of viewport */}
      <section className="partner-hero">
        <div className="hero-image">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
            alt="Partnership Opportunities with The Shapes United" 
          />
          <div className="hero-overlay">
            <div className="container">
              <div className="hero-content">
                <h1>Partner With Us</h1>
                <p className="hero-subtitle">Join Our Mission to Make Fashion Accessible for Everyone</p>
                <p className="hero-text">
                  Become part of a growing network of healthcare providers, retailers, and institutions 
                  bringing innovative adaptive clothing to communities worldwide.
                </p>
                <a href="#partnership-types" className="hero-cta">Explore Partnership Opportunities</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
    {/* Enhanced Why Partner Section */}
<section className="why-partner-section">
  <div className="container">
    <div className="section-header">
      <div className="section-badge">PARTNERSHIP ADVANTAGES</div>
      <h2>Why Partnering with The Shapes United is Your Best Business Decision</h2>
      <p className="section-subtitle">
        Join a rapidly growing market leader in adaptive fashion with proven success, 
        innovative products, and comprehensive partner support.
      </p>
    </div>

    <div className="partnership-stats">
      <div className="stat-card">
        <div className="stat-number">300%</div>
        <div className="stat-label">Market Growth (3 Years)</div>
        <div className="stat-description">Adaptive clothing sector expansion</div>
      </div>
      <div className="stat-card">
        <div className="stat-number">95%</div>
        <div className="stat-label">Partner Retention Rate</div>
        <div className="stat-description">Long-term successful partnerships</div>
      </div>
      <div className="stat-card">
        <div className="stat-number">40%</div>
        <div className="stat-label">Higher Margins</div>
        <div className="stat-description">Compared to standard apparel</div>
      </div>
      <div className="stat-card">
        <div className="stat-number">24-48h</div>
        <div className="stat-label">Response Time</div>
        <div className="stat-description">Dedicated partner support</div>
      </div>
    </div>

    <div className="competitive-advantages">
      <h3>Our Competitive Edge</h3>
      <div className="advantages-grid">
        <div className="advantage-card premium">
          <div className="advantage-icon">👑</div>
          <div className="advantage-content">
            <h4>Market Leadership Position</h4>
            <p>We're the fastest-growing adaptive fashion brand in Australia, with proven market acceptance and growing international demand.</p>
            <ul className="advantage-points">
              <li>First-mover advantage in premium adaptive fashion</li>
              <li>Multiple industry awards and recognition</li>
              <li>Established NDIS and healthcare relationships</li>
              <li>Growing media presence and brand awareness</li>
            </ul>
          </div>
        </div>

        <div className="advantage-card innovative">
          <div className="advantage-icon">💡</div>
          <div className="advantage-content">
            <h4>Patented Innovation & IP</h4>
            <p>Our proprietary designs and technology give you exclusive access to products competitors can't match.</p>
            <ul className="advantage-points">
              <li>3 patented adaptive clothing technologies</li>
              <li>Proprietary magnetic closure systems</li>
              <li>Exclusive sensory-friendly fabric blends</li>
              <li>Continuous R&D investment (15% of revenue)</li>
            </ul>
          </div>
        </div>

        <div className="advantage-card support">
          <div className="advantage-icon">🤝</div>
          <div className="advantage-content">
            <h4>Unmatched Partner Support</h4>
            <p>We invest in your success with resources most brands can't provide.</p>
            <ul className="advantage-points">
              <li>Dedicated partnership success manager</li>
              <li>Comprehensive training programs</li>
              <li>Co-marketing budget allocation</li>
              <li>Quarterly business review sessions</li>
              <li>Priority technical support</li>
            </ul>
          </div>
        </div>

        <div className="advantage-card financial">
          <div className="advantage-icon">💰</div>
          <div className="advantage-content">
            <h4>Superior Financial Returns</h4>
            <p>Our partnerships deliver better margins and faster inventory turns than traditional fashion.</p>
            <ul className="advantage-points">
              <li>45-55% gross margins for partners</li>
              <li>Fast inventory turnover (60-90 days)</li>
              <li>Consistent reorder rates (75%+)</li>
              <li>Seasonal inventory protection</li>
              <li>Flexible payment terms available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div className="market-opportunity">
      <div className="opportunity-content">
        <div className="opportunity-text">
          <h3>The Adaptive Fashion Market Opportunity</h3>
          <p>With 1 in 5 Australians living with disability and an aging population, the adaptive clothing market represents a $4.2 billion opportunity in Australia alone, growing at 12% annually.</p>
          
          <div className="market-facts">
            <div className="market-fact">
              <div className="fact-number">4.4M</div>
              <div className="fact-text">Australians with disability</div>
            </div>
            <div className="market-fact">
              <div className="fact-number">$4.2B</div>
              <div className="fact-text">Australian market size</div>
            </div>
            <div className="market-fact">
              <div className="fact-number">12%</div>
              <div className="fact-text">Annual growth rate</div>
            </div>
          </div>
        </div>
        <div className="opportunity-chart">
          <div className="chart-container">
            <div className="chart-bar" style={{height: '100%', background: 'linear-gradient(to top, #667eea, #764ba2)'}}>
              <div className="chart-label">Adaptive Fashion</div>
              <div className="chart-value">12% Growth</div>
            </div>
            <div className="chart-bar" style={{height: '70%', background: 'linear-gradient(to top, #4CAF50, #2E7D32)'}}>
              <div className="chart-label">General Fashion</div>
              <div className="chart-value">3% Growth</div>
            </div>
          </div>
          <div className="chart-legend">
            <div className="legend-item">
              <div className="legend-color adaptive"></div>
              <span>Adaptive Clothing Market</span>
            </div>
            <div className="legend-item">
              <div className="legend-color general"></div>
              <span>General Fashion Market</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="partner-success">
      <h3>Proven Partner Success Stories</h3>
      <div className="success-stories">
        <div className="success-story">
          <div className="story-header">
            <div className="story-icon">🏥</div>
            <div className="story-title">
              <h4>Melbourne Rehabilitation Centre</h4>
              <div className="story-subtitle">Healthcare Provider - Partnership since 2021</div>
            </div>
          </div>
          <div className="story-content">
            <p>"In just 2 years, The Shapes United has become our primary adaptive clothing supplier. Patient satisfaction increased by 40%, and our staff save an average of 2 hours daily on dressing assistance."</p>
            <div className="story-metrics">
              <div className="metric">
                <div className="metric-value">40%</div>
                <div className="metric-label">Patient Satisfaction Increase</div>
              </div>
              <div className="metric">
                <div className="metric-value">2 hours</div>
                <div className="metric-label">Daily Staff Time Saved</div>
              </div>
            </div>
          </div>
        </div>

        <div className="success-story">
          <div className="story-header">
            <div className="story-icon">🛍️</div>
            <div className="story-title">
              <h4>Inclusive Fashion Boutique Sydney</h4>
              <div className="story-subtitle">Retail Partner - Partnership since 2022</div>
            </div>
          </div>
          <div className="story-content">
            <p>"Our TSU collection consistently outperforms other brands by 3:1. The quality and customer support are exceptional, and our customers keep coming back."</p>
            <div className="story-metrics">
              <div className="metric">
                <div className="metric-value">3:1</div>
                <div className="metric-label">Sales vs. Other Brands</div>
              </div>
              <div className="metric">
                <div className="metric-value">85%</div>
                <div className="metric-label">Repeat Customer Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="partner-cta-banner">
      <div className="cta-banner-content">
        <h3>Ready to Grow Your Business with Us?</h3>
        <p>Join 50+ successful partners who are already benefiting from our market-leading adaptive fashion solutions.</p>
        <div className="cta-banner-actions">
          <a href="#partner-form" className="cta-banner-btn primary">
            Apply for Partnership
          </a>
          <a href="/case-studies" className="cta-banner-btn secondary">
            View Full Case Studies
          </a>
        </div>
      </div>
    </div>
  </div>
</section>
      {/* Partnership Types */}
      <section id="partnership-types" className="partnership-types-section">
        <div className="container">
          <div className="section-header">
            <h2>Partnership Opportunities</h2>
            <p className="section-subtitle">Choose the partnership model that fits your organization</p>
          </div>

          <div className="tabs-container">
            <div className="tabs-header">
              {partnerTypes.map(type => (
                <button
                  key={type.id}
                  className={`tab-btn ${activeTab === type.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(type.id)}
                >
                  <span className="tab-icon">{type.icon}</span>
                  <span className="tab-title">{type.title}</span>
                </button>
              ))}
            </div>

            <div className="tab-content">
              {partnerTypes.map(type => (
                <div 
                  key={type.id}
                  className={`tab-panel ${activeTab === type.id ? 'active' : ''}`}
                >
                  <div className="panel-header">
                    <h3>{type.title}</h3>
                    <p className="panel-description">{type.description}</p>
                  </div>

                  <div className="panel-content">
                    <div className="benefits-list">
                      <h4>Partnership Benefits</h4>
                      <ul>
                        {type.benefits.map((benefit, index) => (
                          <li key={index}>
                            <span className="check-icon">✓</span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="ideal-for">
                      <h4>Ideal For</h4>
                      <div className="ideal-tags">
                        {type.idealFor.map((item, index) => (
                          <span key={index} className="ideal-tag">{item}</span>
                        ))}
                      </div>
                    </div>

                    <div className="panel-cta">
                      <a href="#partner-form" className="apply-btn">
                        Apply for {type.title} Partnership
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Process */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Partnership Process</h2>
            <p className="section-subtitle">Simple steps to becoming a valued partner</p>
          </div>

          <div className="process-timeline">
            {partnershipProcess.map((step, index) => (
              <div key={step.step} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  <div className="step-duration">
                    <span className="duration-icon">⏱️</span>
                    <span>{step.duration}</span>
                  </div>
                </div>
                {index < partnershipProcess.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2>What Our Partners Say</h2>
            <p className="section-subtitle">Hear from organizations already partnering with us</p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-content">
                  <p className="quote">"{testimonial.quote}"</p>
                  <div className="testimonial-author">
                    <div className="author-name">{testimonial.author}</div>
                    <div className="author-role">{testimonial.role}</div>
                    <div className="partner-type">{testimonial.type}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section id="partner-form" className="partner-form-section">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>Start Your Partnership Journey</h2>
              <p>Complete the form below and our partnership team will contact you within 48 hours.</p>
            </div>

            <form onSubmit={handleSubmit} className="partner-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="organization">Organization *</label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    placeholder="Organization name"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Your work email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Your contact number"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="partnerType">Partnership Type *</label>
                  <select
                    id="partnerType"
                    name="partnerType"
                    value={formData.partnerType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select partnership type</option>
                    <option value="healthcare">Healthcare Provider</option>
                    <option value="retail">Retail Partner</option>
                    <option value="corporate">Corporate/Institution</option>
                    <option value="distributor">Distributor</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location *</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="City, Country"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="heardAbout">How did you hear about us?</label>
                <select
                  id="heardAbout"
                  name="heardAbout"
                  value={formData.heardAbout}
                  onChange={handleInputChange}
                >
                  <option value="">Select an option</option>
                  <option value="search">Online Search</option>
                  <option value="social">Social Media</option>
                  <option value="referral">Referral</option>
                  <option value="event">Industry Event</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Tell us about your interest in partnering *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your organization and why you're interested in partnering with The Shapes United..."
                  rows="5"
                  required
                ></textarea>
              </div>

              <div className="form-footer">
                <button type="submit" className="submit-btn">
                  Submit Partnership Inquiry
                </button>
                <p className="form-note">
                  By submitting this form, you agree to our Privacy Policy. Our partnership team will contact you within 48 hours.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="partner-faq">
        <div className="container">
          <div className="section-header">
            <h2>Partnership FAQs</h2>
            <p className="section-subtitle">Common questions about partnering with us</p>
          </div>

          <div className="faq-grid">
            <div className="faq-item">
              <h3>What are the minimum order requirements?</h3>
              <p>Minimum order requirements vary by partnership type. Healthcare providers may have different minimums than retail partners. Contact our team for specific details based on your partnership category.</p>
            </div>

            <div className="faq-item">
              <h3>Do you offer exclusivity arrangements?</h3>
              <p>Yes, we offer exclusive territory arrangements for qualified distributors and retail partners. Terms are negotiated based on market potential and partnership commitment.</p>
            </div>

            <div className="faq-item">
              <h3>What support do you provide partners?</h3>
              <p>We provide comprehensive support including product training, marketing materials, sales tools, co-marketing opportunities, and dedicated account management.</p>
            </div>

            <div className="faq-item">
              <h3>How do you handle shipping and logistics?</h3>
              <p>We handle all shipping and logistics for our partners. We offer bulk shipping options, consolidated shipments, and can work with your existing logistics providers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="partner-cta">
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2>Ready to Start the Conversation?</h2>
              <p>Contact our partnership team directly for immediate assistance.</p>
            </div>
            <div className="cta-actions">
              <a href="mailto:partnerships@theshapesunited.com" className="cta-btn email">
                <span className="btn-icon">✉️</span>
                <span>Email Partnerships Team</span>
              </a>
              <a href="tel:+61212345678" className="cta-btn phone">
                <span className="btn-icon">📞</span>
                <span>Call +61 2 1234 5678</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Partner;